import { Box, Stack, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useNavigate, useSearchParams } from "react-router-dom";
import Iconify from "../../minimals/components/iconify";
import LinearProgressWithLabel from "../shared/LinearProgressWithLabel";
import { fMonthDayYearDotNotation } from "../utils/formatTime";
import DownloadDocumentButton from "./DownloadFileButton";
import OpenFileButton from "./OpenFileButton";
import RecordableActions from "./RecordableActions";
import RecordableHistoryDialog from "./RecordableHistoryDialog";
import UploadButton from "./UploadButton";
import VersionBarcodeButton from "./VersionBarcodeButton";
import VersionCount from "./VersionCount";
import FileIcon from "./components/FileIcon";
import { Recordable, RecordableStatusColorHexMap, Status } from "./types";
import OpenSimplifileDocument from "./OpenSimplifileDocument";
import axios from "axios";
import StickyTable from "../shared/StickyTable";

export default function RecordableTable({
  isDeleted,
  data,
  setSelectedRecordable,
  setOpenVersionUpload,
  openVersionBarcode,
  getAndSetRecordables,
  isReorderMode = false,
  isReorderDisabled = false,
  onReorder
}: {
  isDeleted?: boolean;
  data: Recordable[];
  setSelectedRecordable: Dispatch<SetStateAction<Recordable | null>>;
  setOpenVersionUpload: Dispatch<SetStateAction<boolean>>;
  openVersionBarcode?: (recordable: Recordable) => void;
  getAndSetRecordables: () => Promise<void>;
  isReorderMode?: boolean;
  isReorderDisabled?: boolean;
  onReorder?: (recordables: Recordable[]) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusTypes, setStatusTypes] = useState<Status[]>([]);
  const order = searchParams.get("order");
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  const showReorderMode = isReorderMode && !isDeleted;
  const canDrag = showReorderMode && !isReorderDisabled && Boolean(onReorder);

  const navigateToTasks = (id: number) => {
    navigate(`./tasks?order=${order}&recordable-id=${id}`);
  };

  const getAndSetStatuses = async () => {
    const { data } = await axios.get(`/api/recordablestracking/recordables/getStatusTypes`);
    setStatusTypes(data);
  };

  const handleSelectedRecordableAndOpenUpload = (r: Recordable) => {
    setSelectedRecordable(r);
    setOpenVersionUpload(true);
  };

  const handleOpenVersionOpload = (recordable: Recordable) => {
    setSelectedRecordable(recordable);
    setOpenVersionUpload(true);
  };

  const handleOpenVersionBarcode = (recordable: Recordable) => {
    openVersionBarcode?.(recordable);
  };

  const [selectedRecordableHistory, setSelectedRecordableHistory] = useState<Recordable>();

  const handleNavigateToTasks = (id: number) => {
    if (showReorderMode) return;
    navigateToTasks(id);
  };

  const handleOnDragEnd = async (result: DropResult) => {
    if (!canDrag || !result.destination || result.source.index === result.destination.index) {
      return;
    }

    const reorderedRecordables = [...data];
    const [movedRecordable] = reorderedRecordables.splice(result.source.index, 1);
    reorderedRecordables.splice(result.destination.index, 0, movedRecordable);

    await onReorder?.(reorderedRecordables);
  };

  useEffect(() => {
    getAndSetStatuses();
  }, []);

  return (
    <>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId={isDeleted ? "deleted-recordables" : "active-recordables"} isDropDisabled={!canDrag}>
          {(droppableProvided) => (
            <StickyTable
              sx={{
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "background.neutral"
                }
              }}
            >
              {data.length > 0 && (
                <TableHead>
                  <TableRow>
                    {showReorderMode && <TableCell width={48} />}
                    <TableCell>{isDeleted ? "Deleted Documents" : "Recordable Document"}</TableCell>
                    <TableCell>Task Count</TableCell>
                    <TableCell>Latest Activity</TableCell>
                    <TableCell>Status</TableCell>
                    {!showReorderMode && <TableCell>Action</TableCell>}
                  </TableRow>
                </TableHead>
              )}
              <TableBody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                {data.map((r, index) => {
                  const latestEvent = r.history[0];
                  return (
                    <Draggable key={r.id} draggableId={`recordable-${r.id}`} index={index} isDragDisabled={!canDrag}>
                      {(draggableProvided, snapshot) => (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            ...draggableProvided.draggableProps.style,
                            backgroundColor: snapshot.isDragging ? "background.paper" : "inherit",
                            boxShadow: snapshot.isDragging
                              ? "0 4px 8px 0 rgba(0, 0, 0, 0.16), 0 6px 20px 0 rgba(0, 0, 0, 0.12)"
                              : "none"
                          }}
                        >
                          {showReorderMode && (
                            <TableCell width={48}>
                              <Tooltip title="Reorder">
                                <Box
                                  {...(draggableProvided.dragHandleProps || {})}
                                  sx={{
                                    alignItems: "center",
                                    color: "text.secondary",
                                    cursor: isReorderDisabled ? "wait" : snapshot.isDragging ? "grabbing" : "grab",
                                    display: "flex",
                                    justifyContent: "center",
                                    width: 32
                                  }}
                                >
                                  <Iconify icon="mdi:drag" />
                                </Box>
                              </Tooltip>
                            </TableCell>
                          )}
                          <TableCell onClick={() => handleNavigateToTasks(r.id)} width={"27%"}>
                            <Stack direction="row" spacing={2}>
                              <FileIcon />
                              <Box>
                                <Typography variant="subtitle2">
                                  {r.typeDescription} {r.description && `- ${r.description}`}
                                </Typography>
                                <Tooltip disableFocusListener title="Add">
                                  <VersionCount
                                    versionCount={r.documents.length}
                                    documents={r.documents}
                                    openVersionUpload={() => handleOpenVersionOpload(r)}
                                    openVersionBarcode={
                                      !isDeleted ? () => handleOpenVersionBarcode(r) : undefined
                                    }
                                  />
                                </Tooltip>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell onClick={() => handleNavigateToTasks(r.id)}>
                            <LinearProgressWithLabel
                              totalTasks={r.taskItems.filter((t) => t.status !== 3).length}
                              completedTasks={r.taskItems.filter((t) => t.status === 2).length}
                              isDeleted={isDeleted}
                            />
                          </TableCell>
                          <TableCell width={500}>
                            {latestEvent && (
                              <>
                                <Typography variant="body2" color="GrayText" noWrap>
                                  <Typography
                                    variant="body2"
                                    display="inline-block"
                                    color="info.main"
                                    onClick={() => setSelectedRecordableHistory(r)}
                                    sx={{ cursor: "pointer" }}
                                  >
                                    {latestEvent.eventTypeDescription}
                                  </Typography>{" "}
                                  on {latestEvent?.dateTime && fMonthDayYearDotNotation(latestEvent?.dateTime)}
                                </Typography>
                                <Typography variant="body2" color="GrayText">
                                  by&nbsp;{latestEvent?.username}
                                </Typography>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle2"
                              color={RecordableStatusColorHexMap[r.statusDescription]}
                              noWrap
                            >
                              {r.statusDescription}
                            </Typography>
                          </TableCell>
                          {!showReorderMode && (
                            <TableCell width={"60%"}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <RecordableActions
                                  recordableId={r.id}
                                  navigateToTasks={() => navigateToTasks(r.id)}
                                  momentumDocumentId={r.documents[0].momentumDocumentId}
                                  setOpenVersionUpload={() => handleSelectedRecordableAndOpenUpload(r)}
                                  getAndSetRecordables={getAndSetRecordables}
                                  isDeleted={isDeleted}
                                  statusTypes={statusTypes}
                                />
                                {!isDeleted && openVersionBarcode && (
                                  <VersionBarcodeButton onClick={() => handleOpenVersionBarcode(r)} />
                                )}
                                <UploadButton setOpenVersionUpload={() => handleSelectedRecordableAndOpenUpload(r)} />
                                <DownloadDocumentButton fileId={r.documents[0].momentumDocumentId} />
                                <OpenFileButton
                                  href={`${momentumUrl}opendoc?id=${r.documents[0].momentumDocumentId}`}
                                />
                                <OpenSimplifileDocument href={r.viewPackageUrl || ""} />
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </TableBody>
            </StickyTable>
          )}
        </Droppable>
      </DragDropContext>
      {selectedRecordableHistory && (
        <RecordableHistoryDialog
          recordable={selectedRecordableHistory}
          onClose={() => setSelectedRecordableHistory(undefined)}
        />
      )}
    </>
  );
}
